import { type FC, useCallback, useRef, useState } from 'react';
import { IconButton, toastQueue, Tooltip, TooltipTrigger } from '@launchpad-ui/components';

import { writeTextToClipboard } from '../../../copyTextToClipboard';

export interface CopyButtonProps {
  /** Text written to the clipboard. */
  text: string;
  /** Accessible label and tooltip, e.g. "Copy message". */
  label: string;
  /** Toast title shown on a successful copy. */
  successTitle?: string;
  /** Fired after a successful copy — used for analytics. */
  onCopy?: () => void;
  size?: 'small' | 'medium';
  /** Redesigned transcript uses Greg's two-squares glyph; legacy keeps the clipboard. */
  redesign?: boolean;
}

const COPIED_RESET_MS = 2000;

/**
 * Minimal icon button that copies `text` to the clipboard, confirms with a
 * toast, and briefly swaps to a check icon. Used for per-message copy controls
 * in the Vega conversation thread.
 */
export const CopyButton: FC<CopyButtonProps> = ({
  text,
  label,
  successTitle = 'Copied to clipboard',
  onCopy,
  size = 'small',
  redesign = false,
}) => {
  const [copied, setCopied] = useState(false);
  const resetTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const handleCopy = useCallback(async () => {
    if (!(await writeTextToClipboard(text))) {
      toastQueue.add({ status: 'error', title: 'Failed to copy' });
      return;
    }
    setCopied(true);
    onCopy?.();
    toastQueue.add({ status: 'info', title: successTitle });
    clearTimeout(resetTimeout.current);
    resetTimeout.current = setTimeout(() => setCopied(false), COPIED_RESET_MS);
  }, [text, successTitle, onCopy]);

  return (
    <TooltipTrigger delay={300}>
      <IconButton
        icon={copied ? 'check' : redesign ? 'copy-content' : 'copy-clipboard'}
        aria-label={label}
        variant="minimal"
        size={size}
        onPress={handleCopy}
      />
      <Tooltip placement="top">{copied ? 'Copied!' : label}</Tooltip>
    </TooltipTrigger>
  );
};
